import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, debounceTime } from 'rxjs';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { OrderReturnsService } from '../../../_services/order-returns.service';
import { AlertService } from '../../../_services/alert.service';
import { OrderReturn } from '../../../_interfaces/order-return';
import { returnStatusClass } from '../status-styles';
import { CreateReturnComponent } from './create-return/create-return.component';
import { ManageReturnComponent } from './manage-return/manage-return.component';
import { PaginatorPagesDirective } from '../../../../Shared/_directives/paginator-pages.directive';

@Component({
    selector: 'app-order-returns',
    imports: [PaginatorPagesDirective, CommonModule, FormsModule, MainLayoutComponent, MatIconModule, MatTooltipModule, MatPaginatorModule, MatProgressSpinnerModule, DatePipe],
    templateUrl: './order-returns.component.html',
    styleUrl: './order-returns.component.css'
})
export class OrderReturnsComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly orderReturnsService = inject(OrderReturnsService);
  private readonly alertService = inject(AlertService);

  returns: OrderReturn[] = [];
  count = 0;
  stats: { total: number; requested: number; received: number; completed: number; rejected: number } | null = null;
  pageNumber = 0;
  itemsPerPage = 20;
  loading = true;
  loadError = false;

  keyword = '';
  statusFilter = 'ALL';

  readonly statusOptions = [
    { value: 'ALL', label: 'All' },
    { value: 'REQUESTED', label: 'Requested' },
    { value: 'RECEIVED', label: 'Received' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'REJECTED', label: 'Rejected' },
  ];

  returnStatusClass = returnStatusClass;

  private readonly search$ = new Subject<void>();
  private loadSeq = 0;

  constructor() {
    this.search$.pipe(debounceTime(350), takeUntilDestroyed()).subscribe(() => {
      this.pageNumber = 0;
      this.load();
    });
  }

  ngOnInit(): void {
    this.load();
  }

  get filtersActive(): boolean {
    return !!(this.keyword || this.statusFilter !== 'ALL');
  }

  countFor(value: string): number {
    const s = this.stats;
    if (!s) return 0;
    switch (value) {
      case 'REQUESTED': return s.requested;
      case 'RECEIVED': return s.received;
      case 'COMPLETED': return s.completed;
      case 'REJECTED': return s.rejected;
      default: return s.total;
    }
  }

  load(): void {
    const seq = ++this.loadSeq;
    this.loading = true;
    this.orderReturnsService
      .getOrderReturns({
        pageIndex: this.pageNumber,
        pageSize: this.itemsPerPage,
        status: this.statusFilter !== 'ALL' ? this.statusFilter : undefined,
        keyword: this.keyword.trim() || undefined,
      })
      .subscribe({
        next: (response) => {
          if (seq !== this.loadSeq) return;
          this.returns = response.items;
          this.count = response.count;
          if (response.statusCounts) this.stats = response.statusCounts;
          this.loading = false;
          this.loadError = false;
        },
        error: (error) => {
          if (seq !== this.loadSeq) return;
          this.loading = false;
          this.loadError = true;
          this.alertService.alert(error?.error?.message || 'Failed to load returns', 'error');
        },
      });
  }

  onSearchInput(): void {
    this.search$.next();
  }

  clearSearch(): void {
    this.keyword = '';
    this.pageNumber = 0;
    this.load();
  }

  setStatus(value: string): void {
    this.statusFilter = value;
    this.pageNumber = 0;
    this.load();
  }

  resetFilters(): void {
    this.keyword = '';
    this.statusFilter = 'ALL';
    this.pageNumber = 0;
    this.load();
  }

  updateList(event: PageEvent): void {
    if (this.itemsPerPage !== event.pageSize) {
      this.itemsPerPage = event.pageSize || 20;
      this.pageNumber = 0;
    } else {
      this.pageNumber = event.pageIndex;
    }
    this.load();
  }

  // ---- What a row shows ----

  itemSummary(row: OrderReturn): string {
    const total = (row.items ?? []).reduce((sum, i) => sum + Number(i.quantity), 0);
    const lines = (row.items ?? []).length;
    return `${total} item${total === 1 ? '' : 's'}${lines > 1 ? ` · ${lines} lines` : ''}`;
  }

  restockedCount(row: OrderReturn): number {
    return (row.items ?? []).filter((i) => i.restocked).length;
  }

  needsRestock(row: OrderReturn): boolean {
    return row.status !== 'REJECTED' && (row.items ?? []).some((i) => !i.restocked && i.condition !== 'UNSELLABLE');
  }

  initiatorName(email: string | null): string {
    return (email ?? '').split('@')[0] || 'Unknown';
  }

  statusDot(status: string): string {
    switch (status) {
      case 'COMPLETED': return 'bg-emerald-500';
      case 'RECEIVED': return 'bg-sky-500';
      case 'REJECTED': return 'bg-rose-500';
      default: return 'bg-amber-500';
    }
  }

  // ---- Actions ----

  onCreateReturn(): void {
    this.dialog
      .open(CreateReturnComponent, { width: '860px', maxWidth: '96vw', maxHeight: '92vh' })
      .afterClosed()
      .subscribe((result) => {
        if (result?.status) this.load();
      });
  }

  onManage(row: OrderReturn): void {
    this.dialog
      .open(ManageReturnComponent, { width: '860px', maxWidth: '96vw', maxHeight: '92vh', data: { returnId: row.id } })
      .afterClosed()
      .subscribe((result) => {
        if (result?.status) this.load();
      });
  }
}
